import { useCallback } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { FiAlertCircle } from 'react-icons/fi';
import { Button } from '@/shared/ui/button';
import { useAsyncResource } from '@/shared/hooks/use-async-resource';
import { agreementsApi } from '@/modules/agreements/services/api';
import { isForbidden } from '@/modules/agreements/shared';

/**
 * The pending-agreement gate.
 *
 * An unsigned tenant cannot perform normal business operations. This banner is the visible
 * half of that rule: it reads the subject's own outstanding requirements and, when any are
 * unsatisfied, tells the user plainly and offers the way to resolve them.
 *
 * It is a BANNER, not a blanket block. The server is the authority — it refuses the specific
 * operations that are gated and returns `AGREEMENT_ACCEPTANCE_REQUIRED`. This banner makes
 * that refusal predictable rather than surprising. It never hides the rest of the console,
 * because recovery, signing and logout must remain reachable while blocked.
 *
 * A failed read renders NOTHING. A gate that cannot read its own state must not invent a
 * blocker, and must not claim there is none.
 */
export function PendingAgreementGate(): ReactNode {
  const navigate = useNavigate();
  const load = useCallback(() => agreementsApi.listMyRequirements('tenant'), []);
  const { data, error } = useAsyncResource(load);

  if (error || isForbidden(error)) {
    return null;
  }

  const outstanding = (data?.requirements ?? []).filter((requirement) => !requirement.satisfied);
  const first = outstanding[0];
  if (!first) {
    return null;
  }

  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-3 border-b border-caution bg-caution-soft px-4 py-3 text-sm"
    >
      <FiAlertCircle className="h-4 w-4 shrink-0 text-caution" aria-hidden="true" />
      <p className="text-content">
        <span className="font-medium">
          {outstanding.length} agreement{outstanding.length === 1 ? '' : 's'} require your action.
        </span>{' '}
        <span className="text-content-muted">
          Some operations are blocked until they are completed.
        </span>
      </p>
      <Button
        size="sm"
        onClick={() => {
          void navigate({ to: '/agreements' });
        }}
      >
        Review agreements
      </Button>
      {first.executionId ? (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            void navigate({
              to: '/agreements/executions/$executionId',
              params: { executionId: first.executionId as string },
            });
          }}
        >
          Resolve now
        </Button>
      ) : null}
    </div>
  );
}
